import posthog from 'posthog-js';

/**
 * PostHog analytics (web) — Phase 1.
 *
 * Hosted on PostHog EU Cloud (data residency aligned with the europe-west1
 * Firebase project). The distinct ID everywhere is the Firebase uid.
 *
 * `capture_pageview` is disabled: $pageview is fired manually from a single
 * `useLocation()` effect in App.jsx so the initial load and every SPA route
 * change go through ONE code path (no double-counting).
 *
 * Every export is a safe wrapper: it no-ops when VITE_POSTHOG_KEY is missing
 * (e.g. local dev without a key) and never throws, so analytics can never take
 * down a user flow.
 */

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;

let initialized = false;

export function initPostHog(): void {
  if (!KEY || initialized) return;
  try {
    posthog.init(KEY, {
      api_host: 'https://eu.i.posthog.com',
      // We fire $pageview ourselves (see App.jsx) so SPA navigation and the
      // initial load share a single code path.
      capture_pageview: false,
    });
    initialized = true;
  } catch {
    // Never let analytics init break app boot.
  }
}

export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!KEY) return;
  try {
    posthog.capture(event, properties);
  } catch {
    /* no-op */
  }
}

export function identify(
  distinctId: string,
  properties?: Record<string, unknown>,
  propertiesSetOnce?: Record<string, unknown>,
): void {
  if (!KEY || !distinctId) return;
  try {
    // 2nd arg = $set (every call), 3rd arg = $set_once (first write wins) — the
    // latter is where cohort-time immutable props (signup_platform/_method) go.
    posthog.identify(distinctId, properties, propertiesSetOnce);
  } catch {
    /* no-op */
  }
}

/**
 * Link the current anonymous distinct_id to the new uid at signup so
 * pre-signup activity (anonymous browsing on /f8chat, /i/:token, /circles)
 * stitches to the identified user. Must be called BEFORE identify().
 */
export function alias(id: string): void {
  if (!KEY || !id) return;
  try {
    posthog.alias(id);
  } catch {
    /* no-op */
  }
}

export function reset(): void {
  if (!KEY) return;
  try {
    posthog.reset();
  } catch {
    /* no-op */
  }
}

/**
 * SHA-256 the raw invite token and return the first 16 hex chars. The raw
 * token is never sent to PostHog — only this truncated hash, which is enough
 * to join web signups against the server-side `room_created` /
 * `room_invite_redeemed` events (which hash the same way).
 */
export async function hashInviteToken(token: string): Promise<string | null> {
  try {
    const data = new TextEncoder().encode(token);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 16);
  } catch {
    return null;
  }
}

// decodeURIComponent throws URIError on malformed input (e.g. `%zz`). This
// runs on an attacker-controllable URL path/param, so it must never throw —
// return null and let the caller fall back to the raw match / organic source.
function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

// Pull a room-invite token out of the current URL: either a direct
// /i/:token landing, or a `?continueUrl=/i/:token` redirect target (the shape
// the Register + Login pages carry through the mediation invite flow).
function extractInviteToken(): string | null {
  if (typeof window === 'undefined') return null;
  const { pathname, search } = window.location;
  let m = pathname.match(/^\/i\/([^/?#]+)/);
  if (m) return safeDecode(m[1]);
  const cont = new URLSearchParams(search).get('continueUrl');
  if (cont) {
    m = cont.match(/\/i\/([^/?#]+)/);
    if (m) return safeDecode(m[1]);
  }
  return null;
}

/**
 * Derive signup attribution from the current URL. Returns `room_invite` +
 * the truncated token hash when the signup originated from an invite landing,
 * else `organic`.
 *
 * Fully guarded: this is awaited inside signup()/signInWithGoogle() AFTER the
 * account already exists, so any throw here would surface to the user as a
 * failed signup and lose the signup_completed mirror. It can never throw —
 * any internal error degrades to `organic`.
 */
export async function deriveSignupSource(): Promise<{
  signup_source: 'room_invite' | 'organic';
  invite_token_hash?: string | null;
}> {
  try {
    const token = extractInviteToken();
    if (token) {
      return { signup_source: 'room_invite', invite_token_hash: await hashInviteToken(token) };
    }
  } catch {
    /* fall through to organic */
  }
  return { signup_source: 'organic' };
}

export default posthog;
